/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {Entity} from '@ion/core/src/types/entities';
import {
  DownloadTemplate,
  TemplateColumn,
  TemplateOptions
} from '@ion/core/src/types/templates';
import {
  downloadBlankTemplate,
  DownloadTemplateDeviceTypes
} from '@ion/core/src/xlsx/TemplateHelpers';
import {TertiaryButton} from '../components/buttons/TertiaryButton';
import PulseLoader from '../components/loading/PulseLoader';
import {BodyTypography} from '../typography/BodyTypography';

export type ExportPromptType = DownloadTemplateDeviceTypes & {
  entity: Entity;
  entityLabel?: string;
  templateColumns: TemplateColumn[];
  templateOptions: TemplateOptions;
  isOptionsLoading?: boolean;
  onDownload: (params?: DownloadTemplate) => void;
  isCurrentTheme?: boolean;
  ignoreEntity?: boolean;
  omitDownloadEntityTemplate?: boolean;
}

// defines the column chooser style definitions ...
const useStyles = makeStyles(theme => ({
  container : {
    top                       : '8px',
    display                   : 'flex',
    position                  : 'relative',
    alignItems                : 'center',
    // "Need the excel file?" in the DLS body type, Inter ...
    '& > .MuiTypography-root' : {
      fontFamily : 'Inter',
      fontSize   : 14,
      fontWeight : 400
    }
  }
}));

interface DownloadTemplateTypes extends DownloadTemplate, DownloadTemplateDeviceTypes {
  ignoreEntity?: boolean;
}

/**
 * Provides the Bulk import templates for download.
 *
 * ```typescript
 * import {BulkImportTemplate} from '@ion/dls/src/imports';
 *
 * <BulkImportTemplate entity={Entity.asset}
 *   entityLabel={entityLabel}
 *   templateColumns={templateColumns}
 *   templateOptions={templateOptions} />
 *
 * ```
 *
 * @param entity entity type that is being import
 * @param entityLabel entityLabel
 * @param templateColumns Template basic configurations
 * @param templateOptions Template optional types
 */
export const BulkImportTemplate = ({
  entity,
  entityLabel,
  templateColumns,
  templateOptions,
  onDownload,
  isCurrentTheme = false,
  isValidFile = false,
  isOptionsLoading = false,
  ignoreEntity = false,
  omitDownloadEntityTemplate = false,
  isDeviceImport = false
}: ExportPromptType) => {
  const classes = useStyles();

  const downloadParams: DownloadTemplateTypes = {
    columns        : templateColumns?.filter((item) => !item.hidden),
    title          : entityLabel ?? entity,
    filename       : entityLabel?.toLowerCase(),
    options        : templateOptions,
    ignoreEntity   : ignoreEntity,
    isDeviceImport : isDeviceImport,
    isValidFile,
    entity
  };

  /**
   * @createBlankTemplate
   *
   * Create blank template based on given entity and column configurations
   */
  function createBlankTemplate() {
    if (downloadParams?.rows) {
      delete downloadParams?.rows;
    }
    let itemObj;
    downloadParams.columns = downloadParams.columns.filter((item) => {
      if (item.key === 'id') {
        itemObj = item;
        return false;
      } else {
        return true;
      }
    });
    downloadBlankTemplate({...downloadParams});
    downloadParams.columns.unshift(itemObj);
  }

  /**
   * @createFilledTemplate
   *
   * Create filled template based on given entity and column configurations
   */
  function createFilledTemplate() {
    onDownload?.(downloadParams);
  }

  return (
    <div className={isCurrentTheme ? classes.container : ''}>
      <BodyTypography component='span'>{translate('common:import.needExcel')}</BodyTypography>
      <TertiaryButton disabled={isOptionsLoading} onClick={createBlankTemplate}>{translate('common:import.downloadBlankTemplate')}</TertiaryButton>
      {!omitDownloadEntityTemplate && (
        <TertiaryButton disabled={isOptionsLoading} onClick={createFilledTemplate}>
          {translate('common:import.downloadEntityTemplate', {entity: entityLabel})}
        </TertiaryButton>
      )}
      {isOptionsLoading && <PulseLoader message={translate('common:import.loadingText')} />}
    </div>
  );
};

export default BulkImportTemplate;
